/**
 * **Quem está a carregar esta textura, para o jogo inteiro** (gate V28I.CF.3).
 *
 * ## O defeito que isto corrige
 *
 * As três cenas — a caça, o duelo e o mundo aberto — partilham UM gestor de
 * texturas, porque partilham um `Phaser.Game`. Mas cada uma guardava, só para
 * si, a lista das chaves que tinha pedido. A caça pedia `cannon-anomaly` no
 * `preload`; o mundo aberto abria antes de o `preload` acabar, não via a textura
 * no gestor, e pedia-a também pelo seu próprio carregador. O segundo a terminar
 * encontrava a chave ocupada, e a consola dizia
 * `Texture key already in use: cannon-anomaly`.
 *
 * Não era uma textura a mais: era a mesma textura pedida duas vezes por dois
 * donos que não se conheciam. Uma chave aleatória por pedido esconderia o aviso
 * e duplicaria a imagem na memória; silenciar a consola esconderia o defeito.
 *
 * ## O que isto é
 *
 * Um registo por gestor de texturas — portanto por jogo — das chaves que algum
 * carregador está a buscar agora. Uma cena que peça uma chave:
 *
 * - **já carregada**: recebe-a no mesmo instante;
 * - **em voo noutra cena**: junta-se à espera, e é avisada quando ela chegar;
 * - **que ninguém pediu**: é ela que a pede, e o registo lembra-se disso.
 *
 * Uma textura é criada uma vez e reutilizada por todas as cenas.
 *
 * ## E se o pedido falhar ou a cena fechar
 *
 * A entrada sai do registo quando o carregador acaba, com ou sem a textura, e
 * quando a cena que pediu é desligada. Um pedido que falhou pode ser repetido;
 * nenhuma cena fica à espera de um carregador que já não existe.
 */

/** O que este registo precisa de uma cena. As três cenas do jogo satisfazem-no. */
export interface SharedTextureScene {
  readonly textures: { exists(key: string): boolean };
  readonly load: {
    image(key: string, url: string): unknown;
    once(event: string, handler: () => void): unknown;
    start(): void;
  };
  readonly events: { once(event: string, handler: () => void): unknown };
}

/** Os avisos à espera de cada chave em voo, por gestor de texturas. */
const inFlight = new WeakMap<object, Map<string, ((loaded: boolean) => void)[]>>();

function registryOf(scene: SharedTextureScene): Map<string, ((loaded: boolean) => void)[]> {
  let registry = inFlight.get(scene.textures);
  if (registry === undefined) {
    registry = new Map();
    inFlight.set(scene.textures, registry);
  }
  return registry;
}

/** A resposta de um pedido: já estava, juntou-se a outro, ou foi este que pediu. */
export type SharedTextureOutcome = 'ready' | 'joined' | 'requested';

/**
 * **Pede uma textura em nome de uma cena**, sem nunca a pedir duas vezes.
 *
 * `settled` corre quando o pedido acaba — já, ou quando o carregador que a
 * busca terminar — e diz se a textura ficou. `start` é falso só num
 * `preload`, em que o Phaser arranca o carregador sozinho.
 */
export function requestSharedTexture(
  scene: SharedTextureScene,
  key: string,
  url: string,
  settled?: (loaded: boolean) => void,
  start = true,
): SharedTextureOutcome {
  if (scene.textures.exists(key)) {
    settled?.(true);
    return 'ready';
  }
  const registry = registryOf(scene);
  const waiting = registry.get(key);
  if (waiting !== undefined) {
    if (settled !== undefined) {
      waiting.push(settled);
    }
    return 'joined';
  }
  const listeners: ((loaded: boolean) => void)[] = settled === undefined ? [] : [settled];
  registry.set(key, listeners);
  const settle = (): void => {
    /* Só a entrada DESTE pedido: outra, posterior, tem a sua própria lista. */
    if (registry.get(key) !== listeners) {
      return;
    }
    registry.delete(key);
    /* Todos são avisados, com ou sem textura: ninguém fica à espera para sempre. */
    const loaded = scene.textures.exists(key);
    for (const listener of listeners) {
      listener(loaded);
    }
  };
  scene.load.image(key, url);
  scene.load.once(`filecomplete-image-${key}`, settle);
  /* O carregador acabou — com a textura ou sem ela. Sem ela, pode repetir-se. */
  scene.load.once('complete', settle);
  /* A cena foi desligada antes: ninguém fica à espera de um carregador morto. */
  scene.events.once('shutdown', settle);
  if (start) {
    scene.load.start();
  }
  return 'requested';
}

/** Quantas chaves estão em voo neste jogo. Para as provas; não decide nada. */
export function sharedTexturesInFlight(scene: Pick<SharedTextureScene, 'textures'>): number {
  return inFlight.get(scene.textures)?.size ?? 0;
}
